import type { NotificationCategory } from './categorize';

export interface NotificationRoutable {
  externalId?: string | null;
  url?: string | null;
}

const CATEGORY_LIST_PATH: Record<NotificationCategory, string> = {
  'client-request': '/client-requests',
  vault: '/vault',
};

/** A same-origin path; rejects absolute and protocol-relative (`//host`, `/\\host`) URLs. */
function isInternalPath(url: string): boolean {
  return url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\');
}

/**
 * Resolves where clicking a notification should navigate. `externalId` follows the sibling
 * CRM's `"EntityType:id"` convention and is preferred since it deep-links straight to the
 * record (opens the Client Requests drawer via `?requestId=`, or the Vault file route); the
 * backend's own `url` is used verbatim as a fallback, and the category's list page after that.
 *
 * The id segment can be followed by further `:`-separated metadata (e.g.
 * `clientRequest:<uuid>:created:manager` for an event-specific notification) — capture only
 * up to the next `:`, not the rest of the string, or the trailing segments end up appended to
 * the id and fail backend UUID validation.
 *
 * Vault is the exception to the `externalId`-first order: the backend ships the exact deep link
 * for each Vault event (file preview, list, ...) in `url`, while `externalId` may reference a
 * non-file entity (e.g. the deactivated user on "Files need attention") — so there `url` wins,
 * and `externalId` only fills in when `url` is absent.
 */
export function getNotificationPath(
  n: NotificationRoutable,
  category: NotificationCategory | null,
): string | null {
  if (category === 'vault' && n.url && isInternalPath(n.url)) return n.url;

  const match = /^([a-z_-]+):([^:]+)/i.exec(n.externalId ?? '');
  if (match) {
    const entity = match[1] ?? '';
    const id = match[2] ?? '';
    if (/^client[_-]?request$/i.test(entity)) {
      return `/client-requests?requestId=${encodeURIComponent(id)}`;
    }
    if (/^(vault|file)$/i.test(entity)) {
      return `/vault/${encodeURIComponent(id)}`;
    }
  }

  if (n.url && isInternalPath(n.url)) return n.url;

  return category ? CATEGORY_LIST_PATH[category] : null;
}
